/**
 * Client-side display mirror of `server/utils/payCalc.js` bucket detection
 * and per-item pay rules. Used by `SpecialRefinisherPayBlock` to preview
 * what each pay bucket contributes when SRP is toggled, and by the Services
 * Management catalog form / table for bucket titles.
 *
 * IMPORTANT: this is a DISPLAY mirror. The server's payCalc.js is the
 * authoritative source for actual paycheck math; if the two ever
 * diverge, the server wins. When server bucket rules change, update
 * this file to match — keep the regex / baselines / detection chain
 * byte-equivalent.
 *
 * 2026-10-06 — Job Type is a display label. Pay is decided by the explicit
 * `payBucket` on the line; the jobType / itemName fallback chain in
 * `bucketForItem` runs ONLY for null-bucket legacy lines (retroactive).
 *   'refinish' Refinisher Percentage — net × user payPercentage
 *   'half'     Half                  — net × 0.5
 *   'full'     Full                  — price × qty, no discount
 *   'custom'   Custom                — `customPayCents` × qty, flat; blank →
 *              price × qty (pays like Full)
 *   'other'    LEGACY (frozen)       — price − material-cost baseline; never
 *              written by new code, kept so old lines recompute unchanged
 * 'custom' and 'other' both accumulate into `otherTotal` (the Other column).
 *
 * Server reference: C:\Users\Owner\Coding Projects\BDR_Admin\BDR_Services\server\utils\payCalc.js
 */

// Bucket titles as the catalog form shows them. Keep these the single place
// the words live so the form, the table and any tooltip agree.
export const PAY_BUCKET_LABELS = {
  refinish: "Refinisher Percentage",
  half: "Half (50%)",
  full: "Full (100%)",
  custom: "Custom",
  other: "Other (legacy)",
};

export const PAY_BUCKET_HELP = {
  refinish: "Net line price × the refinisher's pay percentage.",
  half: "A fixed 50% of the net line price — maintenance work.",
  full: "100% of the line price, no discount — tips, Hardware Change, Trip Charge.",
  custom:
    "A flat amount per unit paid straight to the refinisher: no discount, no percentage, split across the crew like every bucket. Blank pays 100% of the price.",
  other: "Retired formula (price − material cost). Saving converts it to Custom.",
};

export function payBucketLabel(bucket) {
  return PAY_BUCKET_LABELS[bucket] || "Not set";
}

// LEGACY material-cost baselines for the frozen 'other' bucket. Mirror of
// server/utils/payCalc.js OTHER_BUCKET_BASELINES_BY_ID / _BY_NAME. Do not
// add entries — set a Custom pay on the Services Management page instead.
const OTHER_BUCKET_BASELINES_BY_ID = {
  "67bfb63eae32b717e2829dd6": 500,   // Weatherstripping — $5/piece
  "66859efc3cc220751507abac": 1000,  // Door Sweep — $10/unit
};
const OTHER_BUCKET_BASELINES_BY_NAME = [
  { match: /weather\s*strip/i, cents: 500 },
  { match: /door\s*sweep/i, cents: 1000 },
];

export function getOtherBaselineCents(item) {
  if (!item) return 0;
  const idKey =
    item.itemId ||
    (item._id != null ? String(item._id) : null) ||
    item.id;
  if (idKey != null) {
    const byId = OTHER_BUCKET_BASELINES_BY_ID[String(idKey)];
    if (byId != null) return byId;
  }
  const name = item.itemName || "";
  for (const entry of OTHER_BUCKET_BASELINES_BY_NAME) {
    if (entry.match.test(name)) return entry.cents;
  }
  return 0;
}

/**
 * Flat per-unit pay for a 'custom' line in integer cents, or null when
 * blank / malformed (→ 100% of price). Mirror of server `customPayCentsFor`:
 * only a non-negative whole-cent string (or integer number) is accepted.
 */
export function customPayCentsFor(item) {
  const raw = item ? item.customPayCents : null;
  if (typeof raw === "number") {
    return Number.isInteger(raw) && raw >= 0 ? raw : null;
  }
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return parseInt(trimmed, 10);
}

/**
 * Return the pay bucket for a single line item. Mirror of server's
 * `bucketFor()` — detection chain and regex are byte-equivalent. The
 * fallback below is the ONLY place jobType influences a bucket, and only
 * for a line with no explicit payBucket (legacy).
 *
 * @param {{ payBucket?: string|null, itemName?: string, jobType?: string, itemId?: string, _id?: string, id?: string }} item
 * @returns {'refinish' | 'half' | 'full' | 'custom' | 'other'}
 */
export function bucketForItem(item) {
  if (
    item.payBucket === "refinish" ||
    item.payBucket === "half" ||
    item.payBucket === "full" ||
    item.payBucket === "custom" ||
    item.payBucket === "other"
  ) {
    return item.payBucket;
  }
  if (item.itemName && item.itemName.trim().toLowerCase() === "tip") {
    return "full";
  }
  if (
    (item.itemName && /hardware change/i.test(item.itemName)) ||
    /hardware change/i.test(item.jobType)
  ) {
    return "full";
  }
  if (item.itemName && /trip\s*charge/i.test(item.itemName)) {
    return "full";
  }
  if (/^\s*other\s*$/i.test(item.jobType || "")) {
    return "other";
  }
  if (
    item.itemName &&
    (/weather\s*strip/i.test(item.itemName) ||
      /door\s*sweep/i.test(item.itemName))
  ) {
    return "other";
  }
  const idKey =
    item.itemId ||
    (item._id != null ? String(item._id) : null) ||
    item.id;
  if (idKey != null && OTHER_BUCKET_BASELINES_BY_ID[String(idKey)] != null) {
    return "other";
  }
  if (/maintenance/i.test(item.jobType)) {
    return "half";
  }
  return "refinish";
}

/**
 * Compute the per-bucket subtotals for a list of items. Used by the SRP
 * preview matrix to show which buckets contribute to what. Returns
 * integer cents per bucket.
 *
 * - Refinish + Half buckets apply `discountPercent` (if provided +
 *   item.discountApplicable is true). Same as server.
 * - Tip / HC / TripCharge ignore discount. Same as server.
 * - Custom pays customPayCents × qty (blank → price × qty), no discount.
 * - Other (legacy) does the per-item baseline subtraction, clamped at 0.
 *
 * `tipTotal`, `hcTotal`, `tripChargeTotal`, and `otherTotal` are the
 * SUMS BEFORE the `/numAssigned` step. The SRP preview multiplies them
 * by 1/numAssigned at display time if numAssigned > 0; otherwise shows
 * the gross.
 *
 * Half-bucket display routing (catch-up 2026-08-18, mirrors server
 * computeJobTotals): a half item's net goes to `halfOtherTotal` when its
 * jobType is "Other" (promo 50/50), to `halfRefinishTotal` when jobType is
 * "Refinish", else to `halfTotal`. Display attribution only — all three pay
 * the identical 50% and ALL three are replaced by an SRP override
 * (2026-10-06; none gets a preview row).
 *
 * @param {Array} items
 * @param {string|number} [discountPercent="0"]
 * @returns {{ refinishTotal:number, halfTotal:number, halfOtherTotal:number, halfRefinishTotal:number, tipTotal:number, hcTotal:number, tripChargeTotal:number, otherTotal:number }}
 */
export function computeBucketSubtotals(items, discountPercent = "0") {
  let refinishTotal = 0;
  let halfTotal = 0;
  let halfOtherTotal = 0;
  let halfRefinishTotal = 0;
  let tipTotal = 0;
  let hcTotal = 0;
  let tripChargeTotal = 0;
  let otherTotal = 0;

  if (!Array.isArray(items)) {
    return { refinishTotal, halfTotal, halfOtherTotal, halfRefinishTotal, tipTotal, hcTotal, tripChargeTotal, otherTotal };
  }

  const pct = parseFloat(discountPercent) || 0;

  for (const item of items) {
    if (!item) continue;
    const bucket = bucketForItem(item);

    if (bucket === "custom") {
      const price = parseInt(item.price) || 0;
      const qty = parseInt(item.quantity) || 1;
      const custom = customPayCentsFor(item);
      const perItem = custom != null ? custom : Math.max(0, price);
      otherTotal += perItem * qty;
      continue;
    }

    if (bucket === "other") {
      const price = parseInt(item.price) || 0;
      const qty = parseInt(item.quantity) || 1;
      const baseline = getOtherBaselineCents(item);
      const perItem = Math.max(0, price - baseline);
      otherTotal += perItem * qty;
      continue;
    }

    if (bucket === "full") {
      const raw = (parseInt(item.price) || 0) * (parseInt(item.quantity) || 1);
      const name = item.itemName || "";
      const isTip = name.trim().toLowerCase() === "tip";
      const isTripCharge = !isTip && /trip\s*charge/i.test(name);
      if (isTip) tipTotal += raw;
      else if (isTripCharge) tripChargeTotal += raw;
      else hcTotal += raw;
      continue;
    }

    const price = parseInt(item.price) || 0;
    const qty = parseInt(item.quantity) || 1;
    const gross = price * qty;
    const net =
      item.discountApplicable && pct > 0
        ? Math.round(gross * (1 - pct / 100))
        : gross;

    if (bucket === "half") {
      if (/^\s*other\s*$/i.test(item.jobType || "")) halfOtherTotal += net;
      else if (/^\s*refinish\s*$/i.test(item.jobType || "")) halfRefinishTotal += net;
      else halfTotal += net;
    } else {
      refinishTotal += net;
    }
  }

  return { refinishTotal, halfTotal, halfOtherTotal, halfRefinishTotal, tipTotal, hcTotal, tripChargeTotal, otherTotal };
}
